import { useState, useRef, useEffect } from 'react';

const AnimatedCard = ({ 
  children, 
  className = '',
  hoverEffect = 'lift',
  delay = 0,
  animation = 'fadeInUp',
  glowColor = 'pink'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = cardRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [delay]);

  const getAnimationClass = () => {
    if (!isVisible) return 'opacity-0 transform translate-y-8';
    
    switch (animation) {
      case 'fadeInUp':
        return 'opacity-100 transform translate-y-0';
      case 'fadeInLeft':
        return 'opacity-100 transform translate-x-0';
      case 'fadeInRight':
        return 'opacity-100 transform translate-x-0';
      case 'scaleIn':
        return 'opacity-100 transform scale-100';
      case 'rotateIn':
        return 'opacity-100 transform rotate-0';
      default:
        return 'opacity-100';
    }
  };

  const getHoverEffect = () => {
    if (!isHovered) return '';
    
    switch (hoverEffect) {
      case 'lift':
        return 'transform -translate-y-1 scale-[1.02]';
      case 'glow':
        return `shadow-xl shadow-${glowColor}-500/30`;
      case 'tilt':
        return 'transform rotate-0.5 scale-[1.02]';
      case 'expand':
        return 'transform scale-[1.03]';
      case 'float':
        return 'transform -translate-y-2';
      default:
        return '';
    }
  };

  const getInitialTransform = () => {
    switch (animation) {
      case 'fadeInLeft':
        return 'translate-x-8';
      case 'fadeInRight':
        return '-translate-x-8';
      case 'scaleIn':
        return 'scale-95';
      case 'rotateIn':
        return 'rotate-12';
      default:
        return 'translate-y-8';
    }
  };

  return (
    <div
      ref={cardRef}
      className={`
        transition-all duration-300 ease-out cursor-pointer
        ${isVisible ? getAnimationClass() : `opacity-0 transform ${getInitialTransform()}`}
        ${getHoverEffect()}
        ${className}
      `}
      style={{ 
        transitionDelay: `${delay}ms`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated border */}
      <div className={`
        absolute inset-0 rounded-lg opacity-0 transition-opacity duration-300
        ${isHovered ? 'opacity-100' : ''}
        bg-gradient-to-r from-pink-500 via-purple-500 to-yellow-400
        animate-gradient-x
      `} style={{ padding: '2px' }}>
        <div className="w-full h-full bg-gray-800 rounded-lg"></div>
      </div>
      
      {/* Card content */}
      <div className="relative z-10">
        {children}
      </div>
      
      {/* Hover glow effect */}
      {isHovered && (
        <div className={`
          absolute inset-0 rounded-lg opacity-20 blur-xl
          bg-gradient-to-r from-${glowColor}-400 to-${glowColor}-600
          animate-pulse
        `}></div>
      )}
    </div>
  );
};

export default AnimatedCard;